import { useQuery } from "@tanstack/react-query"

import type { SprintReport } from "@/types/sprint-report"
import { SCHEMA_VERSION } from "@/types/sprint-report"

// The report data is gated behind the same access-code session when the site is
// private, so send the editor token (localStorage) as a Bearer header — the ADO
// iframe blocks third-party cookies, so the header is what actually carries it.
function authToken(): string | null {
  try {
    return localStorage.getItem("golive-token")
  } catch {
    return null
  }
}

async function fetchCurrentSnapshot(): Promise<SprintReport> {
  const token = authToken()
  const res = await fetch("/api/sprint/current", {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  })
  if (!res.ok) {
    const body = await res.text().catch(() => "")
    throw new Error(`Snapshot request failed: ${res.status} ${res.statusText} ${body}`)
  }
  const payload = (await res.json()) as SprintReport
  if (payload.schema_version !== SCHEMA_VERSION) {
    throw new Error(
      `Schema mismatch — frontend expects ${SCHEMA_VERSION}, server returned ${payload.schema_version}.`,
    )
  }
  return payload
}

export function useSprintReport() {
  return useQuery({
    queryKey: ["sprint", "current"],
    queryFn: fetchCurrentSnapshot,
    refetchInterval: 15 * 60 * 1000, // 15 min — matches backend cron
    refetchOnWindowFocus: true,
    placeholderData: (previous) => previous,
  })
}
